import {
  ConnectStatus,
  ConnectionStatus,
} from "screens/settings/components/connection-status";
import { Color } from "utils/theme";
import { RequestTopics } from "services/topics";
import { DataStatus } from "screens/settings/components/data-status";
import { formatPercentage, formatTemperature } from "utils/formatters";
import { PLACEHOLDER, SYSTEM_STATUS_FETCH_INTERVAL_MS } from "utils/constants";
import useMqttApi from "hooks/use-mqtt-api";
import { useGpsStatus } from "hooks/use-gps-status";
import { toast } from "react-toastify";
import { SystemStatus as ISystemStatus } from "types/system-status";
import { useTranslation } from "react-i18next";
import { StatusSection } from "../components/status-section";
import { ScrollButtons } from "components/ui/scroll-buttons";
import { useEffect, useRef, useState } from "react";
import { TabContainer } from "../components/tab-container";
import { Spinner } from "components/ui/spinner";
import { ErrorState } from "../components/error-state";

export const SystemStatus = ({ title }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "settings.system_status",
  });

  const [status, setStatus] = useState<ISystemStatus | null>(null);

  const gpsConnected = useGpsStatus();

  const ref = useRef<HTMLDivElement>(null);

  const { data, loading, error, fetch } = useMqttApi<{ data: ISystemStatus }>({
    requestTopic: RequestTopics.SystemStatus,
    enabled: true,
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useEffect(() => {
    // fetch status interval
    const interval = setInterval(
      () => fetch(),
      SYSTEM_STATUS_FETCH_INTERVAL_MS
    );

    return () => {
      clearInterval(interval);
    };
  }, [fetch]);

  useEffect(() => {
    // Update state (which might cause re-render and scroll jump)
    if (data?.data) setStatus(data.data);
  }, [data]);

  if (error) {
    return (
      <ErrorState
        title={t("error.title")}
        description={t("error.description")}
        detail={error?.message ?? ""}
        retryLabel={t("error.retry")}
        retrying={loading}
        onRetry={() => fetch()}
      />
    );
  }

  if (loading && !status) {
    return (
      <div className="w-full flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!status) {
    return (
      <div className="w-full flex-1 flex items-center justify-center">
        <p className="p-5" style={{ color: Color.Primary[400] }}>
          {t("no_data_message")}
        </p>
      </div>
    );
  }

  return (
    <TabContainer ref={ref} title={title}>
      <ScrollButtons containerRef={ref} className="absolute bottom-4 right-4" />
      <StatusSection
        title={t("sensors.title")}
        description={t("sensors.description")}
      >
        {status?.sensors.length >= 0 ? (
          status?.sensors.map((sensor) => (
            <ConnectionStatus
              key={sensor.name}
              label={sensor.name}
              status={
                sensor.connected ? ConnectStatus.Online : ConnectStatus.Offline
              }
            />
          ))
        ) : (
          <p className="p-4">{t("sensors.no_data_message")}</p>
        )}
      </StatusSection>
      <StatusSection
        title={t("network.title")}
        description={t("network.description")}
      >
        <ConnectionStatus
          label={t("network.internet")}
          status={
            status?.network.internet
              ? ConnectStatus.Online
              : ConnectStatus.Offline
          }
        />
        <ConnectionStatus
          label={t("network.modem")}
          status={
            status?.network.modem ? ConnectStatus.Online : ConnectStatus.Offline
          }
        />
        <ConnectionStatus
          label={t("network.ethernet_0")}
          status={
            status?.network.eth0?.connected
              ? ConnectStatus.Online
              : ConnectStatus.Offline
          }
        />
        <ConnectionStatus
          label={t("network.ethernet_1")}
          status={
            status?.network.eth1?.connected
              ? ConnectStatus.Online
              : ConnectStatus.Offline
          }
        />
      </StatusSection>
      <StatusSection
        title={t("nmea2000.title")}
        description={t("nmea2000.description")}
      >
        <ConnectionStatus
          label={t("nmea2000.nmea2000_connection")}
          status={
            status?.nmea.connected
              ? ConnectStatus.Online
              : ConnectStatus.Offline
          }
        />
        <ConnectionStatus
          label={t("nmea2000.engine_data")}
          status={
            status?.nmea.engine_data
              ? ConnectStatus.Online
              : ConnectStatus.Offline
          }
        />
        <ConnectionStatus
          label={t("nmea2000.gps")}
          status={gpsConnected ? ConnectStatus.Online : ConnectStatus.Offline}
        />
      </StatusSection>
      <StatusSection
        title={t("hardware.title")}
        description={t("hardware.description")}
      >
        <ConnectionStatus
          label={t("hardware.hdmi")}
          status={
            status?.hardware.hdmi ? ConnectStatus.Online : ConnectStatus.Offline
          }
        />
        <DataStatus
          label={t("hardware.storage_usage")}
          value={
            status?.hardware.storage_usage !== undefined
              ? formatPercentage(status?.hardware.storage_usage / 100) + "%"
              : PLACEHOLDER
          }
        />
        <DataStatus
          label={t("hardware.computer_temperature")}
          value={
            status?.hardware.computer_temperature
              ? formatTemperature(
                  status?.hardware.computer_temperature.toString()
                ) + " °C"
              : PLACEHOLDER
          }
        />
      </StatusSection>
    </TabContainer>
  );
};
